<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useApiTabStore, type ParamsField } from '@/stores/apiTab.store'
import { BODY_TYPES } from '@/utils/apiTab'
import { RequestSection } from '@/utils/apiTab'
import apiTabService from '@/services/ide/apiTab.service'
import checkBoxIcon from '@/assets/images/ide/check.svg'

const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  }
})
const apiTabStore = useApiTabStore()
const rows = ref([{ key: '', value: '', description: '', active: false }])

/**
 * Type guard to determine if the provided data is an array of ParamsField objects.
 * @param data - The data to be checked.
 * @returns - Returns true if `data` is an array of `ParamsField` objects; otherwise false.
 */
function isFormDataUrlEncodedFieldArray(data: any): data is ParamsField[] {
  return (
    Array.isArray(data) &&
    data.every(
      (item: any) => 'key' in item && 'value' in item && 'description' in item && 'active' in item
    )
  )
}
/**
 * Adds a new empty row if the specified index is the last row and the current row has non-empty key, value, or description.
 * @param index - The index of the row to check.
 */
const addRowIfLast = (index: any) => {
  if (
    index === rows.value.length - 1 &&
    (rows.value[index].key !== '' ||
      rows.value[index].value !== '' ||
      rows.value[index].description !== '')
  ) {
    rows.value.push({ key: '', value: '', description: '', active: false })
  }
}

/**
 * Retrieves form data in URL-encoded format from the store based on the
 * active request index. If valid form data is found, it updates the
 * `rows` array with the retrieved data. If no data is found, or if the
 * retrieved data is invalid, it initializes the `rows` array with a
 * default empty parameter object.
 */
const retrieveValuesFromStore = () => {
  const formDataUrlEncodedData = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.BODY,
    BODY_TYPES.FORM_URLENCODED
  )

  if (isFormDataUrlEncodedFieldArray(formDataUrlEncodedData)) {
    if (formDataUrlEncodedData.length > 0) {
      rows.value = [...formDataUrlEncodedData]
    } else {
      rows.value = [{ key: '', value: '', description: '', active: false }]
    }
  } else {
    rows.value = [{ key: '', value: '', description: '', active: false }]
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => rows.value,
  (newRows) => {
    newRows.forEach((_, index) => {
      apiTabService.removeRowIfEmpty(rows.value, index)
    })
    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.BODY,
      rows.value,
      BODY_TYPES.FORM_URLENCODED
    )
  },
  { deep: true }
)

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)
</script>

<template>
  <div class="mx-auto w-full max-w-full overflow-auto">
    <div class="flex w-full min-w-[500px] flex-col lg:min-w-fit lg:pr-0">
      <!-- Table header -->
      <div
        class="stroke-secondary-default flex flex-row rounded-tl rounded-tr border-l border-t font-bold"
      >
        <div
          class="text-high stroke-secondary-default w-1/5 border-r p-2 text-center font-medium leading-snug"
        >
          Active
        </div>
        <div
          class="text-high stroke-secondary-default w-2/5 border-r p-2 text-center font-medium leading-snug"
        >
          Key
        </div>
        <div
          class="text-high stroke-secondary-default w-2/5 border-r p-2 text-center font-medium leading-snug"
        >
          Value
        </div>
        <div
          class="text-high stroke-secondary-default w-2/5 border-r p-2 text-center font-medium leading-snug"
        >
          Description
        </div>
      </div>

      <div
        v-for="(row, index) in rows"
        :key="index"
        :class="[
          'flex flex-row ',
          { 'stroke-secondary-default border-t': index === 0 },
          { ' stroke-secondary-default': index === rows.length - 1 },
          { 'border-b-0': index !== rows.length - 1 }
        ]"
      >
        <div
          :class="{
            'rounded-bl-lg': index === rows.length - 1
          }"
          class="stroke-secondary-default w-1/5 border p-2 text-center dark:bg-new-neutral-700/50"
        >
          <div
            v-if="row.key !== '' || row.value !== '' || row.description !== ''"
            class="flex items-center justify-center"
          >
            <label class="relative flex cursor-pointer items-center">
              <input
                type="checkbox"
                v-model="row.active"
                class="absolute h-full w-full cursor-pointer opacity-0"
              />

              <div
                class="background-primary dark:background-primary flex h-4 w-4 items-center justify-center rounded bg-new-secondary"
              >
                <img
                  v-if="row.active"
                  :src="row.active ? checkBoxIcon : ''"
                  class="relative h-3.5 w-3.5"
                />
              </div>
            </label>
          </div>
        </div>
        <input
          type="text"
          v-model="row.key"
          placeholder="Key"
          class="stroke-secondary-default text-mid w-2/5 border bg-inherit p-2 text-center font-light leading-snug outline-none dark:bg-new-neutral-700/50"
          @input="addRowIfLast(index)"
        />
        <input
          type="text"
          v-model="row.value"
          placeholder="Value"
          class="text-mig stroke-secondary-default w-2/5 border bg-inherit p-2 text-center font-light leading-snug outline-none dark:bg-new-neutral-700/50"
          @input="addRowIfLast(index)"
        />
        <input
          type="text"
          v-model="row.description"
          placeholder="Description"
          :class="{
            'rounded-br-lg': index === rows.length - 1
          }"
          class="text-mig stroke-secondary-default w-2/5 border bg-inherit p-2 text-center font-light leading-snug outline-none dark:bg-new-neutral-700/50"
          @input="addRowIfLast(index)"
        />
      </div>
    </div>
  </div>
</template>
